import { Box, Stack, Typography } from '@mui/joy';
import { Envelope, EnvelopeOpen, X } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { memo, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components/Button';
import Tooltip from 'core/elements/Tooltip';
import { formatRelativeDate } from 'core/utils/date';
import { BellNotificationSeverity, BellNotificationSurface } from 'notifications/enums';
import { IBellNotification, IBellNotificationCta } from 'notifications/types';
import { getNotificationContent } from 'notifications/utils/notification';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { NotificationContent } from '../NotificationContent';
import { NotificationTypeIcon } from '../NotificationTypeIcon';

interface IProps {
  notification: IBellNotification;
  onClick: (notification: IBellNotification) => void;
  onDismiss: (id: number) => void;
  onAction: (notification: IBellNotification, cta: IBellNotificationCta) => void;
  onToggleRead: (notification: IBellNotification) => void;
}

const CONTROL_ICON_SIZE = 16;
const ROW_CONTROLS_CLASS = 'notificationRowControls';

// Screen-reader only: the unread state is otherwise conveyed by the dot and the tint.
// Pixel strings: a bare 1 in sx means 100%, which made each label a drawer-sized box that
// stretched the scroll area.
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

// Opacity, not display: the hidden controls stay in the tab order and reveal on focus.
const ROW_CONTROLS_REVEAL = {
  [`&:hover .${ROW_CONTROLS_CLASS}, &:focus-within .${ROW_CONTROLS_CLASS}`]: { opacity: 1 },
  ['@media (hover: none)']: { [`& .${ROW_CONTROLS_CLASS}`]: { opacity: 1 } },
} as const;

const getUnreadBackground = (isError: boolean) => (isError ? 'danger.50' : 'primary.50');

export const NotificationDrawerRow = memo(function NotificationDrawerRow({
  notification,
  onClick,
  onDismiss,
  onAction,
  onToggleRead,
}: IProps) {
  const { t } = useTranslation();

  const { severity } = getNotificationContent(notification);
  const isUnread = !notification.isRead;
  const isError = BellNotificationSeverity.ERROR === severity;
  const toggleReadLabel = t(isUnread ? 'buttonLabelMarkAsRead' : 'buttonLabelMarkAsUnread');

  const onRowClick = () => onClick(notification);

  const handleOnDismiss = (event: MouseEvent) => {
    event.stopPropagation();
    onDismiss(notification.id);
  };

  const handleOnToggleRead = (event: MouseEvent) => {
    event.stopPropagation();
    onToggleRead(notification);
  };

  return (
    <Stack
      data-testid="notificationDrawerRow"
      direction={Direction.ROW}
      onClick={onRowClick}
      sx={{
        gap: 1.5,
        paddingX: 3,
        paddingY: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        backgroundColor: isUnread ? getUnreadBackground(isError) : 'transparent',
        '&:hover': { backgroundColor: 'background.level1' },
        ...ROW_CONTROLS_REVEAL,
      }}
    >
      <NotificationTypeIcon type={notification.type} severity={severity} isUnread={isUnread} />

      <NotificationContent
        notification={notification}
        surface={BellNotificationSurface.DRAWER}
        onAction={onAction}
        titleStart={
          isUnread && (
            <Box component="span" sx={VISUALLY_HIDDEN}>
              {t('labelUnreadNotification')}
            </Box>
          )
        }
        beforeActions={
          // Parsed here: createdAt carries its offset, which the helper's server-zone parse
          // would drop.
          <Typography level={Level.BODY_XS} sx={{ color: 'text.tertiary' }}>
            {formatRelativeDate(notification.createdAt ? dayjs(notification.createdAt) : undefined)}
          </Typography>
        }
      />

      <Stack
        className={ROW_CONTROLS_CLASS}
        direction={Direction.ROW}
        sx={{
          flexShrink: 0,
          alignItems: 'flex-start',
          gap: 0.5,
          opacity: 0,
          transition: 'opacity 150ms ease-out',
        }}
      >
        <Tooltip title={toggleReadLabel} followCursor={false}>
          <Button
            onClick={handleOnToggleRead}
            icon={
              isUnread ? (
                <EnvelopeOpen size={CONTROL_ICON_SIZE} />
              ) : (
                <Envelope size={CONTROL_ICON_SIZE} />
              )
            }
            aria-label={toggleReadLabel}
            variant={Variant.OUTLINED}
            color={Color.NEUTRAL}
          />
        </Tooltip>

        <Tooltip title={t('buttonLabelClear')} followCursor={false}>
          <Button
            onClick={handleOnDismiss}
            icon={<X size={CONTROL_ICON_SIZE} />}
            aria-label={t('buttonLabelClear')}
            variant={Variant.OUTLINED}
            color={Color.DANGER}
          />
        </Tooltip>
      </Stack>
    </Stack>
  );
});
